<script setup lang="ts">
  import { useCssModule } from 'vue';

  export interface RadioVariantProps {
    /**
     * Whether the input should be disabled
     */
    disabled?: boolean;

    /**
     * Whether the group should expand to the parent's width
     */
    fullWidth?: boolean;

    /**
     * A unique string to distinguish one Radio instance from another
     * and to link the label to the input; required for accessibility
     */
    id: string | number;

    /**
     * The description which appears to the right of the radio button
     */
    label?: string;

    /**
     * value bound to the parent component
     */
    modelValue?: string | undefined;

    /**
     * Passed to the "name" attribute of the `<input>` element.
     */
    name?: string;

    /**
     * The value for the radio input.
     */
    value: string;
  }

  const props = defineProps<RadioVariantProps>();
  const classes = useCssModule();

  const emit = defineEmits<{
    (e: 'input', event: Event): void;
  }>();
</script>

<template>
  <div class="flex" :class="[classes.root, { 'w-full': props.fullWidth }]">
    <input
      :id="`${props.name}-${props.id}`"
      :class="classes.input"
      type="radio"
      :name="props.name"
      :value="props.value"
      :checked="props.modelValue === props.value"
      :disabled="props.disabled"
      @input="emit('input', $event)"
    />
    <label
      v-show="props.label"
      :class="[classes.label, { 'text-ice-500': props.disabled }]"
      :for="`${props.name}-${props.id}`"
    >
      {{ props.label }}
    </label>
  </div>
</template>

<style module>
  @reference "../../../../styles/main.css";

  .label {
    font-weight: var(--stash-font-weight-medium);
    user-select: none;
    cursor: pointer;
    padding: 0 --spacing(3);
  }

  .input {
    appearance: none;
    border-radius: 50%;
    min-width: 20px;
    width: 20px;
    height: 20px;
    border: 1px solid var(--color-ice-500);
    transition: all 0.2s;
    position: relative;
    cursor: pointer;
  }

  .input:checked {
    background-image: radial-gradient(var(--color-blue-500) 50%, transparent 54%);
  }

  .input:disabled {
    background-image: radial-gradient(var(--color-ice-100) 100%, var(--color-ice-100) 100%);
  }

  .input:disabled:checked {
    background-image: radial-gradient(var(--color-ice-500) 50%, var(--color-ice-100) 54%);
  }

  .input:hover:not(:disabled) {
    border-color: var(--color-blue-500);
  }

  .input:disabled ~ .label {
    cursor: auto;
  }

  .input:hover:not(:disabled) ~ .label {
    color: var(--color-ice-900);
  }

  .input:not(:disabled) ~ .label:hover {
    color: var(--color-ice-900);
  }
</style>
